/**
 * Authoritative web-access check (mobile-only billing).
 *
 * Glycemic Snap has no web checkout — Pro is bought in the mobile app through
 * RevenueCat. The `syncPremiumStatus` callable verifies the LIVE RevenueCat
 * `Glycemic Snap Pro` entitlement server-side and returns the verified value.
 *
 * The client-writable `fsnap_users.isPremium` mirror is NEVER consulted here:
 * a non-Pro user could write it themselves. Anything short of a successful
 * server verdict is `unverified`, which the gate treats as "not granted, try
 * again", never as access. Every paid Cloud Function independently re-verifies
 * as well.
 */
import { httpsCallable } from 'firebase/functions'
import { auth, functions } from '@/lib/firebase'

export type WebAccessResult = 'granted' | 'denied' | 'unverified' | 'no_profile'

interface SyncPremiumStatusResponse {
  isPremium: boolean | null
  status?: 'verified' | 'unverified' | 'no_profile'
}

export async function fetchWebAccess(): Promise<WebAccessResult> {
  const uid = auth.currentUser?.uid
  if (!uid) return 'denied'
  try {
    const fn = httpsCallable<{ isPremiumClient: boolean }, SyncPremiumStatusResponse>(functions, 'syncPremiumStatus')
    const res = (await fn({ isPremiumClient: false })).data
    if (res?.status === 'no_profile') return 'no_profile'
    if (res?.status === 'verified' && typeof res.isPremium === 'boolean') return res.isPremium ? 'granted' : 'denied'
    return 'unverified'
  } catch {
    return 'unverified'
  }
}
